import { useState, useCallback, useEffect, useRef } from 'react';
import { firebaseAppointmentService, Appointment } from '../services/firebase/FirebaseAppointmentService';
import { formatTime12Hour } from '../utils/timeFormat';
import { DictationController } from '../services/DictationController';

interface UseVoiceCommandsProps {
  providerId?: string;
  onTabChange: (tab: string) => void;
}

// Map of spoken phrases → tab IDs (covering all sidebar sections + natural speech)
const NAV_COMMANDS: Record<string, { tab: string; response: string }> = {
  // Client / Patient Care — multiple natural phrases per target
  'overview':              { tab: 'overview',    response: 'Opening dashboard overview.' },
  'dashboard':             { tab: 'overview',    response: 'Opening dashboard overview.' },
  'home':                  { tab: 'overview',    response: 'Opening dashboard overview.' },
  'main page':             { tab: 'overview',    response: 'Opening dashboard overview.' },
  'appointments':          { tab: 'schedule',    response: 'Opening appointments and schedule.' },
  'schedule':              { tab: 'schedule',    response: 'Opening appointments and schedule.' },
  'calendar':              { tab: 'schedule',    response: 'Opening appointments and schedule.' },
  'my schedule':           { tab: 'schedule',    response: 'Opening your schedule.' },
  'todays schedule':       { tab: 'schedule',    response: 'Opening your schedule.' },
  'clients':               { tab: 'clients',     response: 'Opening clients.' },
  'patients':              { tab: 'clients',     response: 'Opening clients.' },
  'my patients':           { tab: 'clients',     response: 'Opening your patients.' },
  'client list':           { tab: 'clients',     response: 'Opening client list.' },
  'patient list':          { tab: 'clients',     response: 'Opening patient list.' },
  'inquiries':             { tab: 'inquiries',   response: 'Opening inquiries.' },
  'new inquiries':         { tab: 'inquiries',   response: 'Opening new inquiries.' },
  'sessions':              { tab: 'sessions',    response: 'Opening session management.' },
  'prescriptions':         { tab: 'prescriptions', response: 'Opening prescriptions.' },
  'meds':                  { tab: 'prescriptions', response: 'Opening prescriptions.' },
  'medications':           { tab: 'prescriptions', response: 'Opening prescriptions.' },
  'messages':              { tab: 'messages',    response: 'Opening messages.' },
  'messaging':             { tab: 'messages',    response: 'Opening messages.' },
  'inbox':                 { tab: 'messages',    response: 'Opening messages.' },
  'my messages':           { tab: 'messages',    response: 'Opening your messages.' },
  'team chat':             { tab: 'team-chat',   response: 'Opening team chat.' },
  'group chat':            { tab: 'team-chat',   response: 'Opening team chat.' },
  'emergency cancellations': { tab: 'emergency-cancellations', response: 'Opening emergency cancellations.' },
  'cancellations':         { tab: 'emergency-cancellations', response: 'Opening emergency cancellations.' },

  // Practice Management
  'tasks':                 { tab: 'tasks',       response: 'Opening tasks.' },
  'to do':                 { tab: 'tasks',       response: 'Opening tasks.' },
  'to-do list':            { tab: 'tasks',       response: 'Opening your to-do list.' },
  'practice activity':     { tab: 'client-activity', response: 'Opening practice activity.' },
  'activity':              { tab: 'client-activity', response: 'Opening practice activity.' },
  'activity log':          { tab: 'client-activity', response: 'Opening activity log.' },
  'billing':               { tab: 'billing',     response: 'Opening billing.' },
  'invoices':              { tab: 'billing',     response: 'Opening billing.' },
  'payments':              { tab: 'billing',     response: 'Opening billing.' },
  'analytics':             { tab: 'analytics',   response: 'Opening analytics.' },
  'reports':               { tab: 'analytics',   response: 'Opening analytics and reports.' },
  'stats':                 { tab: 'analytics',   response: 'Opening analytics.' },
  'staff meetings':        { tab: 'meetings',    response: 'Opening staff meetings.' },
  'meetings':              { tab: 'meetings',    response: 'Opening staff meetings.' },
  'team meetings':         { tab: 'meetings',    response: 'Opening team meetings.' },
  'hr':                    { tab: 'hr',          response: 'Opening H R and documents.' },
  'hr management':         { tab: 'hr',          response: 'Opening H R management.' },
  'h.r.':                  { tab: 'hr',          response: 'Opening H R and documents.' },
  'human resources':       { tab: 'hr',          response: 'Opening H R and documents.' },
  'documents':             { tab: 'hr',          response: 'Opening H R and documents.' },
  'settings':              { tab: 'settings',    response: 'Opening settings.' },
  'preferences':           { tab: 'settings',    response: 'Opening settings.' },
};

/**
 * Clinic Voice — the provider-side assistant voice.
 * Uses ElevenLabs TTS API for natural-sounding speech (same quality as Hope, different voice).
 * Falls back to Web Speech API if ElevenLabs is unavailable.
 * Clinic: professional, clear, slightly faster — distinct from Hope's warm, nurturing tone.
 */
class ClinicVoice {
  private currentAudio: HTMLAudioElement | null = null;
  private isSpeaking = false;
  private speakQueue: string[] = [];
  private audioUnlocked = false;
  private audioContext: AudioContext | null = null;
  private pendingGreeting: string | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      // Unlock audio on first user interaction (click/touch/keydown)
      const unlock = () => {
        if (this.audioUnlocked) return;
        // Create and resume an AudioContext to unlock autoplay
        try {
          this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          this.audioContext.resume();
          // Also play a silent audio element to unlock HTMLAudioElement playback
          const silent = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=');
          silent.volume = 0;
          silent.play().catch(() => {});
        } catch { /* silent */ }
        this.audioUnlocked = true;
        // Play any queued greeting that was waiting for unlock
        if (this.pendingGreeting) {
          const text = this.pendingGreeting;
          this.pendingGreeting = null;
          setTimeout(() => this.speak(text), 300);
        }
      };
      document.addEventListener('click', unlock, { once: false });
      document.addEventListener('touchstart', unlock, { once: false });
      document.addEventListener('keydown', unlock, { once: false });
    }
  }

  initialize() {
    // No-op — audio unlocking happens on user interaction
  }

  async speak(text: string) {
    if (!text) return;

    // If audio isn't unlocked yet, queue for playback on first user interaction
    if (!this.audioUnlocked) {
      this.pendingGreeting = text;
      return;
    }

    // Stop current speech
    this.stop();

    // If already speaking, queue
    if (this.isSpeaking) {
      this.speakQueue.push(text);
      return;
    }

    this.isSpeaking = true;

    try {
      const response = await fetch('/api/elevenlabs-tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: 'hope' }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.warn('[ClinicVoice] ElevenLabs error', response.status, errText, '— falling back to Web Speech');
        this.isSpeaking = false;
        this.speakWebFallback(text);
        return;
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.volume = 0.85;
      this.currentAudio = audio;

      audio.onended = () => {
        this.isSpeaking = false;
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        if (this.speakQueue.length > 0) {
          this.speak(this.speakQueue.shift()!);
        }
      };

      audio.onerror = () => {
        this.isSpeaking = false;
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
      };

      await audio.play();
    } catch (error) {
      console.warn('[ClinicVoice] ElevenLabs play failed, using Web Speech:', error);
      this.isSpeaking = false;
      this.speakWebFallback(text);
    }
  }

  private speakWebFallback(text: string) {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 0.85;
    window.speechSynthesis.speak(utterance);
  }

  stop() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    this.isSpeaking = false;
    this.speakQueue = [];
  }

  get isInitialized() { return this.audioUnlocked; }
  get voiceName() { return 'Clinic (ElevenLabs)'; }
}

// Singleton Clinic voice instance
const clinicVoice = new ClinicVoice();

export const useVoiceCommands = ({ providerId, onTabChange }: UseVoiceCommandsProps) => {
  const [wakeWordEnabled, setWakeWordEnabled] = useState(true);
  const [speechInitialized, setSpeechInitialized] = useState(false);
  const todayAppointmentsRef = useRef<Appointment[]>([]);

  // Load today's appointments from Firestore for voice queries
  useEffect(() => {
    if (!providerId) return;

    const loadTodayAppointments = async () => {
      try {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

        const appointments = await firebaseAppointmentService.getAppointments({
          providerId,
          startDate: startOfDay,
          endDate: endOfDay,
        });

        // Filter to scheduled/confirmed only
        todayAppointmentsRef.current = appointments.filter(
          a => a.status === 'scheduled' || a.status === 'confirmed'
        );
      } catch (err) {
        console.warn('[VoiceCommands] Failed to load appointments:', err);
      }
    };

    loadTodayAppointments();
    // Refresh every 5 minutes
    const interval = setInterval(loadTodayAppointments, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [providerId]);

  // Stop Clinic voice when wake word is disabled
  useEffect(() => {
    if (!wakeWordEnabled) {
      clinicVoice.stop();
    }
  }, [wakeWordEnabled]);

  const initializeSpeech = useCallback(() => {
    if (!speechInitialized) {
      clinicVoice.initialize();
      setSpeechInitialized(true);
    }
  }, [speechInitialized]);

  // Clinic's own voice — distinct from Hope
  // Gated behind wakeWordEnabled so toggling off silences all responses immediately
  const speak = useCallback((text: string) => {
    if (!wakeWordEnabled) return;
    clinicVoice.speak(text);
  }, [wakeWordEnabled]);

  // Greeting always plays regardless of wake word setting
  const speakGreeting = useCallback((text: string) => {
    clinicVoice.speak(text);
  }, []);

  const handleWakeWordDetected = useCallback(() => {
    speak("Yes, how can I help you?");
  }, [speak]);

  const handleVoiceCommand = useCallback((command: string) => {
    const cmd = command.toLowerCase().trim();

    // --- Dictation ---
    // Checked before everything else on purpose. Once dictation starts, speech
    // routes to the note rather than through here (see WakeWordDetector), so
    // this branch only ever handles starting it.
    if (/\b(start|begin|open|new)\s+(a\s+)?dictation\b/.test(cmd) ||
        /\bstart\s+dictating\b/.test(cmd) ||
        /\bdictate\s+(a\s+)?(note|birp|soap|dap)\b/.test(cmd) ||
        /\btake\s+(a\s+)?note\b/.test(cmd)) {
      if (DictationController.isActive()) {
        speak('Already dictating. Say stop dictation when you are finished.');
        return;
      }

      // Honor a spoken format ("dictate a SOAP note"); BIRP is the default.
      const templateMatch = cmd.match(/\b(birp|soap|dap)\b/);
      const template = templateMatch ? templateMatch[1].toUpperCase() : 'BIRP';

      DictationController.start({ template });
      speak(`Dictation started for a ${template} note. Say stop dictation when you are finished.`);
      return;
    }

    // Stopping is handled by the speech listener while dictation is active, but
    // a stop can still land here if the session already ended.
    if (/\bstop\s+dictat/.test(cmd) || /\bend\s+dictat/.test(cmd)) {
      if (DictationController.isActive()) {
        DictationController.stop();
        speak('Dictation stopped. Writing up your note.');
      } else {
        speak('No dictation is running.');
      }
      return;
    }

    // --- Stop speech (just be quiet, don't disable wake word) ---
    if (cmd === 'stop' || cmd === 'be quiet' || cmd === 'shut up' || cmd === 'quiet' ||
        cmd === 'clinic stop' || cmd === 'hey clinic stop' || cmd === 'okay stop' ||
        cmd === 'thats enough' || cmd === "that's enough" || cmd === 'enough' ||
        cmd === 'shh' || cmd === 'hush' || cmd === 'never mind' || cmd === 'nevermind') {
      clinicVoice.stop();
      return; // Silent — no response needed
    }

    // --- Disable voice recognition entirely ---
    if (cmd.includes('stop listening') || cmd.includes('turn off') || cmd.includes('disable voice') ||
        cmd.includes('go away') || cmd.includes('turn off clinic')) {
      setWakeWordEnabled(false);
      speak("Voice recognition stopped. Click the microphone button to start again.");
      return;
    }

    // --- Navigation commands ---
    const navPrefixes = [
      'go to ', 'show ', 'open ', 'take me to ', 'navigate to ',
      'pull up ', 'show me ', 'let me see ', 'i need ', 'bring up ',
      'switch to ', 'jump to ', 'head to ', 'get me to ',
      'can you open ', 'can you show ', 'please open ', 'please show ',
    ];
    let navTarget = '';

    for (const prefix of navPrefixes) {
      if (cmd.includes(prefix)) {
        navTarget = cmd.substring(cmd.indexOf(prefix) + prefix.length).trim();
        break;
      }
    }

    if (!navTarget) navTarget = cmd;
    navTarget = navTarget.replace(/^hey\s+clinic\s*/i, '').replace(/^clinic\s*/i, '').replace(/\s*please$/i, '').trim();

    for (const [phrase, config] of Object.entries(NAV_COMMANDS)) {
      if (navTarget.includes(phrase) || cmd.includes(phrase)) {
        onTabChange(config.tab);
        speak(config.response);
        return;
      }
    }

    // --- Appointment queries ---
    if (cmd.includes('appointment') || cmd.includes('calendar') || cmd.includes('schedule')) {
      if (cmd.includes('what') || cmd.includes('do i have') || cmd.includes('how many') || cmd.includes('any')) {
        const appointments = todayAppointmentsRef.current;
        if (appointments.length === 0) {
          speak("You have no upcoming appointments for today.");
        } else {
          let response = `You have ${appointments.length} appointment${appointments.length === 1 ? '' : 's'} today. `;
          appointments.slice(0, 5).forEach((apt, index) => {
            const time = formatTime12Hour(apt.startTime || '');
            const name = apt.patientName || 'a patient';
            response += `${index + 1}. ${name} at ${time}. `;
          });
          speak(response);
        }
        return;
      }

      if (cmd.includes('next') || cmd.includes("who's next") || cmd.includes('whos next')) {
        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const next = todayAppointmentsRef.current.find(a => (a.startTime || '') >= currentTime);
        if (next) {
          const time = formatTime12Hour(next.startTime || '');
          speak(`Your next appointment is with ${next.patientName || 'a patient'} at ${time}.`);
        } else {
          speak("You have no more appointments today.");
        }
        return;
      }
    }

    // --- Time query ---
    if (cmd.includes('what time') || cmd.includes('current time') || cmd.includes('the time')) {
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      speak(`The current time is ${timeString}.`);
      return;
    }

    // --- Help ---
    if (cmd.includes('help') || cmd.includes('what can you do') || cmd.includes('commands') || cmd.includes('what do you do')) {
      speak("I can help you navigate. Try: Go to appointments, Show me clients, Pull up billing, Open messages, Take me to H R. You can ask about appointments: What are my appointments? Who's next? You can also say: What time is it? And to quiet me, just say Stop or Be quiet.");
      return;
    }

    // --- Unrecognized ---
    speak("I didn't catch that. Try saying Go to followed by a section name, or say Help for a list of commands.");
  }, [onTabChange, speak]);

  const toggleWakeWord = useCallback(() => {
    setWakeWordEnabled(!wakeWordEnabled);
  }, [wakeWordEnabled]);

  const disableClinic = useCallback(() => {
    clinicVoice.stop();
    setWakeWordEnabled(false);
  }, []);

  const enableClinic = useCallback(() => {
    setWakeWordEnabled(true);
  }, []);

  return {
    wakeWordEnabled,
    speechInitialized,
    initializeSpeech,
    speak,
    speakGreeting,
    handleWakeWordDetected,
    handleVoiceCommand,
    toggleWakeWord,
    disableClinic,
    enableClinic
  };
};
